import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { Marquee } from "@/components/site/Marquee";
import { ServiceIcon } from "@/components/site/ServiceIcon";
import { CaseCard } from "@/components/site/CaseCard";
import { StepForm } from "@/components/site/StepForm";
import { Testimonials } from "@/components/site/Testimonials";
import { CtaBand } from "@/components/site/CtaBand";
import { HomeFaq, homeFaqLd } from "@/components/home/HomeFaq";
import { SectionHead } from "@/components/site/SectionHead";
import { HomeHero } from "@/components/home/HomeHero";
import { BewlFeature } from "@/components/home/BewlFeature";
import { WhySection } from "@/components/home/WhySection";
import { FounderSection } from "@/components/home/FounderSection";
import { Sparkle } from "@/components/site/Sparkle";
import { useSettings, useContent, SITE_URL } from "@/lib/api";
import { SERVICE_LINKS, EXTRA_PRICES } from "@/data/site";

const AD_NOTE = "Advertising spend is payable separately and directly to the advertising platform.";

const CardPrice = ({ p, dark }) => {
  if (!p) return null;
  const monthly = p.group !== "One-Off Services";
  return (
    <div className={`mt-5 border-t pt-4 ${dark ? "border-white/15" : "border-violet-200"}`} data-testid="card-price">
      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${monthly ? (dark ? "bg-white/15 text-white" : "bg-[#EDE9FE] text-[#581C87]") : (dark ? "bg-[#2563EB] text-white" : "bg-[#DBEAFE] text-[#1E40AF]")}`}>{monthly ? "Monthly" : "One-off"}</span>
      <p className={`mt-2 text-lg font-extrabold ${dark ? "text-white" : "text-[#13052A]"}`}>{p.price}</p>
      {p.ad_spend && <p className={`mt-1 text-xs leading-snug ${dark ? "text-violet-200" : "text-slate-500"}`}>{AD_NOTE}</p>}
    </div>
  );
};

const ExtraPrices = ({ items }) => {
  if (!items.length) return null;
  return (
    <Reveal className="mt-4 flex flex-col gap-3 rounded-[24px] border border-violet-200 p-5 sm:flex-row sm:items-center sm:gap-6">
      <p className="text-sm font-extrabold text-[#13052A]">Also available</p>
      <ul className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-8" data-testid="extra-prices">
        {items.map((p) => (
          <li key={p.name} className="flex items-center gap-2 text-sm text-slate-700">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${p.group === "One-Off Services" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#EDE9FE] text-[#581C87]"}`}>{p.group === "One-Off Services" ? "One-off" : "Monthly"}</span>
            {p.name.replace(" & Maintenance", "")}: <span className="font-extrabold text-[#13052A]">{p.price}</span>
          </li>
        ))}
      </ul>
    </Reveal>
  );
};

const Services = ({ pricing }) => {
  const priceOf = (name) => pricing.find((p) => p.name === name);
  return (
  <section id="services" className="scroll-mt-24 bg-white py-16 sm:py-28" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <SectionHead eyebrow="SEO, PPC & Paid Social Services" title={<>Everything your marketing needs, <span className="serif-accent font-normal text-[#581C87]">handled by one expert.</span></>} />
        <Reveal><Link to="/pricing" className="btn-ghost" data-testid="services-pricing-link">Full pricing <ArrowRight className="h-4 w-4" /></Link></Reveal>
      </div>
      <div className="mt-10 grid gap-4 sm:mt-14 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICE_LINKS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 4) * 0.06} className="h-full">
            <Link to={`/${s.slug}${s.anchor ? `#${s.anchor}` : ""}`} data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[28px] border p-7 transition-[transform,box-shadow,background-color] duration-500 hover:-translate-y-1.5 sm:min-h-[260px] ${i === 0 ? "border-transparent bg-[#581C87] text-white hover:shadow-[0_30px_60px_-30px_rgba(88,28,135,.9)]" : "border-violet-200 bg-[#FBFAFF] hover:bg-white hover:shadow-[0_30px_60px_-30px_rgba(46,16,101,.35)]"}`}>
              <div className="flex items-start justify-between">
                <span className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors duration-500 ${i === 0 ? "bg-white/15 text-white" : "bg-[#EDE9FE] text-[#581C87] group-hover:bg-[#7C3AED] group-hover:text-white"}`}>
                  <ServiceIcon name={s.icon} className="h-5 w-5" />
                </span>
              </div>
              <h3 className={`mt-auto pt-8 text-xl font-extrabold ${i === 0 ? "text-white" : "text-[#13052A]"}`}>{s.title}</h3>
              <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "text-violet-100" : "text-slate-600"}`}>{s.short}</p>
              <CardPrice p={priceOf(s.priceName)} dark={i === 0} />
              <ArrowUpRight className={`absolute right-6 top-24 h-5 w-5 opacity-0 transition-[opacity,transform] duration-500 group-hover:-translate-y-1 group-hover:opacity-100 ${i === 0 ? "text-white" : "text-[#7C3AED]"}`} />
            </Link>
          </Reveal>
        ))}
      </div>
      <ExtraPrices items={EXTRA_PRICES.map(priceOf).filter(Boolean)} />
      <Reveal className="mt-10 rounded-[28px] bg-[#F5F0FF] p-7 sm:p-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-testid="pricing-note">
          <div className="max-w-2xl">
            <p className="text-lg font-bold leading-snug text-[#13052A] sm:text-xl">Every business is different. Our services are tailored to your goals, budget and ambitions. No unnecessary extras, just the support you actually need.</p>
            <p className="mt-3 text-sm text-slate-600">Prices are starting rates unless otherwise stated. Advertising budgets are paid separately and directly to the relevant platforms.</p>
          </div>
          <Link to="/contact" className="btn-accent shrink-0 !px-7 !py-4 text-base" data-testid="services-talk-cta">Let&rsquo;s Talk About Your Business <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

const Results = ({ cases }) => (
  <section className="relative overflow-hidden bg-[#13052A] py-16 sm:py-28" data-testid="results-preview">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <SectionHead dark eyebrow="Digital Marketing Results & Case Studies" title={<>Real Campaigns. <span className="serif-accent font-normal text-[#C4B5FD]">Real Results.</span></>}
          text="A selection of recent client work across fitness, events, e-commerce and professional training." />
      </div>
      <div className="mt-10 grid gap-5 sm:mt-14 md:grid-cols-2 lg:grid-cols-3">
        {cases.filter((c) => c.home_order).sort((a, b) => a.home_order - b.home_order).slice(0, 6).map((c, i) => <Reveal key={c.slug} delay={(i % 3) * 0.08} className="h-full"><CaseCard c={c} i={i} /></Reveal>)}
      </div>
      <Reveal className="mt-10 flex justify-center">
        <Link to="/results" className="btn-accent" data-testid="home-view-all-cases">View All Case Studies <ArrowUpRight className="h-4 w-4" /></Link>
      </Reveal>
    </div>
  </section>
);

const AUDIT_POINTS = ["Google Ads: wasted spend & missed searches", "Meta Ads: audiences, creative & tracking", "SEO: visibility & technical health", "Website: speed, mobile & conversion barriers", "Tracking: GA4, GTM & conversions"];

const AuditSection = () => (
  <section id="free-audit" className="relative scroll-mt-20 overflow-hidden bg-gradient-to-br from-[#581C87] via-[#4C1D95] to-[#1E3A8A] py-16 text-white sm:py-28" data-testid="audit-section">
    <div className="grid-lines pointer-events-none absolute inset-0 opacity-50" />
    <div className="relative mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
      <div>
        <Reveal>
          <p className="eyebrow text-violet-200"><span className="h-1.5 w-1.5 rounded-full bg-[#FF7043]" /> Free Digital Audit</p>
          <h2 className="mt-5 text-3xl font-extrabold leading-[1.04] sm:text-5xl lg:text-6xl">Is Your Digital Marketing Working as Hard as It <span className="serif-accent font-normal text-[#C4B5FD]">Should?</span></h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-violet-100 md:text-lg">Get a free initial review of your digital marketing, with potential improvements highlighted across:</p>
        </Reveal>
        <ul className="mt-7 space-y-3.5">
          {AUDIT_POINTS.map((p, i) => (
            <Reveal key={p} delay={i * 0.06}>
              <li className="flex items-start gap-3 text-base font-semibold"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#FF7043]" />{p}</li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.3}><p className="mt-8 max-w-md text-sm text-violet-200">No obligation. Reviewed personally by Freya, not an automated report.</p></Reveal>
      </div>
      <Reveal delay={0.1}><StepForm formType="audit" /></Reveal>
    </div>
  </section>
);

export default function HomePage() {
  const s = useSettings();
  const { items: cases } = useContent("case_studies");
  const { items: testimonials } = useContent("testimonials");
  const { items: pricing } = useContent("pricing");
  return (
    <>
      <Seo raw title="Digital AdDoctor | SEO, PPC & Meta Ads, Tunbridge Wells" path="/"
        description="Freelance SEO & PPC consultant in Tunbridge Wells. Digital AdDoctor helps businesses grow with Google Ads, Facebook & Instagram Ads and results-driven SEO."
        jsonLd={[{ "@type": "WebSite", "@id": `${SITE_URL}/#website`, name: "Digital AdDoctor", url: `${SITE_URL}/`, publisher: { "@id": `${SITE_URL}/#business` } }, homeFaqLd]} />
      <HomeHero s={s} />
      <BewlFeature />
      <Services pricing={pricing} />
      <Results cases={cases} />
      <WhySection />
      <AuditSection />
      <FounderSection />
      <section className="bg-[#F5F0FF] py-16 sm:py-28" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="Testimonials" title={<>What clients say <Sparkle className="serif-accent font-normal text-[#581C87]">about working with Freya.</Sparkle></>} />
          <div className="mt-10 sm:mt-14"><Testimonials items={testimonials} /></div>
        </div>
      </section>
      <Marquee />
      <HomeFaq />
      <CtaBand />
    </>
  );
}
